/*
 * <variant-content>
 * Swaps vrac-style content blocks (notes/goûts/analyses, terroir, recipe, pack)
 * when the selected variant changes, using content pre-rendered per variant into
 * a JSON blob at page load. No network request is made on variant change.
 *
 * Relies on the bubbling `on:variant:change` CustomEvent dispatched by the
 * product form / variant picker (detail.variant.id).
 */
class VariantContent extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.swipers = {};
    this._lastRenderedId = null;
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.data = this.parseData();
    this._lastRenderedId = this.data ? String(this.data.selectedVariantId) : null;

    this.bindFaq();

    // Swiper may not have executed yet (deferred scripts); init once the page is ready.
    if (document.readyState === "complete") {
      this.initVisibleBlocks();
    } else {
      window.addEventListener("load", () => this.initVisibleBlocks(), { once: true });
    }

    document.addEventListener("on:variant:change", this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener("on:variant:change", this.onVariantChange);
    this.destroySwipers();
  }

  parseData() {
    const dataEl = this.querySelector(".variant-content__data");
    if (!dataEl) return null;
    try {
      return JSON.parse(dataEl.textContent || "{}");
    } catch (err) {
      return null;
    }
  }

  get blocks() {
    return Array.from(this.querySelectorAll("[data-variant-block]"));
  }

  onVariantChange(event) {
    const id = event && event.detail && event.detail.variant && event.detail.variant.id;
    if (id == null) return;
    this.render(String(id));
  }

  render(variantId) {
    if (!this.data || !this.data.variants) return;
    if (variantId === this._lastRenderedId) return;

    const entry = this.data.variants[variantId];
    // No content for this variant: keep whatever is currently shown (graceful fallback).
    if (!entry) return;

    this._lastRenderedId = variantId;

    // Pack variants hide the whole section; single variants show it again.
    const section = this.closest(".section-w-variant-vrac");
    if (section) section.hidden = entry.isPack === true;

    this.blocks.forEach((block) => {
      const type = block.dataset.variantBlock;
      const html = (entry[type] || "").trim();

      this.destroySwiper(type);

      if (html === "") {
        block.hidden = true;
        block.innerHTML = "";
        return;
      }

      block.hidden = false;
      block.innerHTML = html;
      this.initBlock(block, type);
    });
  }

  initVisibleBlocks() {
    this.blocks.forEach((block) => {
      if (block.hidden) return;
      this.initBlock(block, block.dataset.variantBlock);
    });
  }

  initBlock(block, type) {
    if (type === "recipe") {
      this.initRecipeSwiper(block, type);
    }
  }

  initRecipeSwiper(block, type) {
    if (typeof Swiper === "undefined") return;
    const el = block.querySelector(".swiper");
    if (!el) return;

    this.swipers[type] = new Swiper(el, {
      direction: "horizontal",
      loop: false,
      slidesPerView: "auto",
      pagination: {
        el: block.querySelector(".swiper-pagination"),
      },
      navigation: {
        nextEl: block.querySelector(".swiper-button-next"),
        prevEl: block.querySelector(".swiper-button-prev"),
      },
    });
  }

  destroySwiper(type) {
    const instance = this.swipers[type];
    if (instance && typeof instance.destroy === "function") {
      instance.destroy(true, true);
    }
    delete this.swipers[type];
  }

  destroySwipers() {
    Object.keys(this.swipers).forEach((type) => this.destroySwiper(type));
  }

  /*
   * Delegated collapsible behaviour, mirroring the original SectionDesc:
   * on desktop (>778px) the panels are always visible via CSS, so we only
   * toggle on mobile. Delegation keeps this working after innerHTML swaps.
   */
  bindFaq() {
    this.addEventListener("click", (e) => {
      const title = e.target.closest(".faq-item-title");
      if (!title || !this.contains(title)) return;
      if (window.innerWidth > 778) return;

      const close = title.querySelector(".faq-close");
      if (close) close.classList.toggle("faq-display-none");

      const parent = title.parentNode;
      if (parent) parent.classList.toggle("faq-item-active");

      const item = parent ? parent.querySelector(".faq-item-text") : null;
      if (item && typeof item.slideToggle === "function") {
        item.slideToggle("250");
      } else if (item) {
        item.style.display = item.style.display === "none" ? "block" : "none";
      }
    });
  }
}

if (!customElements.get("variant-content")) {
  customElements.define("variant-content", VariantContent);
}
